import { Component } from "react";
import { captureException } from "extensions/sentry";

import { amount, number, rate } from "common/formats";
import { FETCH_SUMMARY_DATA } from "common/actions/dashboard";
import { Placeholder } from "common/components/placeholder";
import { HelpHover } from "common/components/helpHover";
import { getDateRangeAsString, drillThrough, formatAmountWithCurrency } from "../widgetHelper";

export class SummaryBarWithChange extends Component {
    static getCount(data, key, count = "count") {
        const record = data.find((i) => i.name === key);
        return record && record[count] ? record[count] : 0;
    }
    
    constructor(props) {
        super(props);
        
        this.state = {
            prevLoaded: false,
            change: 0
        };
    }
    
    componentDidMount() {
        const prevRange = this.props.dateRanges[1];
    
        if (this.existingPromiseRequest) {
            this.existingPromiseRequest.cancel();
        }
    
        this.existingPromiseRequest = FETCH_SUMMARY_DATA({
            url: this.props.subModel.get("api_endpoint")([this.props.first_pivot_key]),
            start: prevRange.startDate.toISOString(),
            end: prevRange.endDate.toISOString(),
            q: this.props.widgetQualifiers
        }).then((data) => {
            const currentPeriodTotal = SummaryBarWithChange.getCount(this.props.data, this.props.pivotName);
            const prevTotals = this.getPreviousTotals(data);
            const prevCount = SummaryBarWithChange.getCount(prevTotals, this.props.pivotName, "count");
            
            // TODO ..pull this into own method for loading secondary count fields
            const reactProps = this.props.subModel.get("doublePivot").reactProps;
            const count2field = reactProps.otherCountFields ? reactProps.otherCountFields[0] : null;
            const currentPeriodTotal2 = count2field ? SummaryBarWithChange.getCount(this.props.data, this.props.pivotName, count2field) : 0;
            const prevCount2 = count2field ? SummaryBarWithChange.getCount(prevTotals, this.props.pivotName, count2field) : 0;
            
            if (!this.props.destroyed) {
                this.setState({
                    prevLoaded: true,
                    prevCount,
                    change: prevCount === 0 ? "-%" : ((currentPeriodTotal - prevCount) / prevCount) * 100,
                    prevCount2,
                    change2: prevCount2 === 0 ? "-%" : ((currentPeriodTotal2 - prevCount2) / prevCount2) * 100
                });
            }
        }).catch((error) => {
            // Add action later if needed, Catching the error to prevent unhandledrejection exception
            if (error.response) {
                window.console.error(error);
            } else {
                captureException(error);
            }
        });
    }
    
    componentWillUnmount() {
        if (this.existingPromiseRequest) {
            this.existingPromiseRequest.cancel();
        }
    }
    
    getPreviousTotals(data) {
        const tabPivot = this.props.first_pivot_key;
        const countField = this.props.subModel.get("doublePivot").reactProps.countField;
        return data.map((item) => ({
            tabPivot, countField, name: item[tabPivot], count: item[countField], ...item
        }));
    }
    
    getValueOrLoader(format, value, isChange = false, isAmount = false) {
        if (this.state.prevLoaded) {
            if (isAmount) {
                return formatAmountWithCurrency(this.state[value], 10_000_000);
            }
            if (this.state[value] === "-%") {
                return this.state[value];
            }
            return isChange && this.state[value] > 0 ? `+${format(this.state[value])}` : format(this.state[value]);
        }
        return <Placeholder />;
    }
    
    getStyle() {
        const summaryStyle = this.props.subModel.get("doublePivot").reactProps.summaryStyle;
        
        if (summaryStyle === 2) {
            return this.getStyle2();
        }
        if (summaryStyle === 3) {
            return this.getStyle3();
        }
        return this.getStyle1();
    }
    
    getStyle1() {
        return (
            <div className="wrap row summary-row">
                <div className="col-xs-4">
                    <div className="summary-block">
                        <div className="block-name">
                            <HelpHover size="sm" title="Chargeback count" body={`Total chargebacks received between ${getDateRangeAsString(this.props.dateRanges[0])}`}>
                                <div className="chargebacksCountLegend"/>
                                <span>CHARGEBACK COUNT </span>
                            </HelpHover>
                        </div>
                        <div className="block-value">{number(SummaryBarWithChange.getCount(this.props.data, this.props.pivotName))}</div>
                    </div>
                </div>
                <div className="col-xs-4">
                    <div className="summary-block">
                        <div className="block-name">
                            <HelpHover size="sm" title="Prior period" body={`Total chargebacks received between ${getDateRangeAsString(this.props.dateRanges[1])}`}>
                                <div className="priorChargebacksCountLegend"/>
                                <span>PRIOR PERIOD </span>
                            </HelpHover>
                        </div>
                        <div className="block-value prior">{this.getValueOrLoader(number, "prevCount", false, false)}</div>
                    </div>
                </div>
                <div className="col-xs-4">
                    <div className="summary-block">
                        <div className="block-name">
                            <HelpHover size="sm" title="Change" body="The percent change from the prior period to the current period">
                                <span>CHANGE </span>
                            </HelpHover>
                        </div>
                        <div className="change-value">
                            {this.getValueOrLoader(rate, "change", true)}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    
    getStyle2() {
        return (
            <div className="wrap row summary-row">
                <div className="col-xs-4">
                    <div className="summary-block">
                        <div className="block-name">
                            <HelpHover size="sm" title="DECLINES" body={`Total declines between ${getDateRangeAsString(this.props.dateRanges[1])}`}>
                                <span className="value-label">DECLINES</span>
                            </HelpHover>
                        </div>
                        <div className="block-value"><span>{number(SummaryBarWithChange.getCount(this.props.data, this.props.pivotName))}</span></div>
                    </div>
                </div>
                <div className="col-xs-4">
                    <div className="summary-block">
                        <div className="block-name">
                            <HelpHover size="sm" title="PRIOR PERIOD" body={`Total declines between ${getDateRangeAsString(this.props.dateRanges[1])}`}>
                                <span className="value-label">PRIOR PERIOD</span>
                            </HelpHover>
                        </div>
                        <div className="block-value"><span>{this.getValueOrLoader(number, "prevCount")}</span></div>
                    </div>
                </div>
                <div className="col-xs-4">
                    <div className="summary-block">
                        <div className="block-name">
                            <HelpHover size="sm" title="CHANGE" body="The percent change from the prior period to the current period">
                                <span className="value-label">CHANGE</span>
                            </HelpHover>
                        </div>
                        <div className="block-value">
                            <span className="change-value">
                                {this.getValueOrLoader(rate, "change", true)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    
    getStyle3() {
        const { summaryTitle, tooltipPrefix, otherCountFields } = this.props.subModel.get("doublePivot").reactProps;
        
        return (
            <div className="wrap row summary-row">
                <div className="col-xs-4">
                    <div className="summary-block">
                        <div className="block-name title">
                            <HelpHover size="sm" topOffset="-2px" title={summaryTitle} body={`${tooltipPrefix} transactions occurring between ${getDateRangeAsString(this.props.dateRanges[0])}`}>
                                <span className="value-label">{summaryTitle}</span>
                            </HelpHover>
                        </div>
                        <div className="block-name">
                            <div className="current primary">{number(SummaryBarWithChange.getCount(this.props.data, this.props.pivotName))}</div>
                            <div className="current secondary">{formatAmountWithCurrency(SummaryBarWithChange.getCount(this.props.data, this.props.pivotName, otherCountFields[0]), 1_000_000)}</div>
                        </div>
                    </div>
                </div>
                <div className="col-xs-4">
                    <div className="summary-block">
                        <div className="block-name title">
                            <HelpHover size="sm" topOffset="-2px" title="Prior period" body={`${tooltipPrefix} transactions occurring between ${getDateRangeAsString(this.props.dateRanges[1])}`}>
                                <span className="value-label">PRIOR PERIOD</span>
                            </HelpHover>
                        </div>
                        <div className="block-name">
                            <div>
                                <div className="prior primary">{this.getValueOrLoader(number, "prevCount")}</div>
                                <div>{this.getValueOrLoader(amount, "prevCount2", false, true)}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-xs-4">
                    <div className="summary-block">
                        <div className="block-name title">
                            <HelpHover size="sm" topOffset="-2px" title="CHANGE" body="The percent change from the prior period to the current period">
                                <span className="value-label">CHANGE</span>
                            </HelpHover>
                        </div>
                        <div className="block-name change">
                            <div>
                                <div className="primary change-value">{this.getValueOrLoader(rate, "change", true)}</div>
                                <div className="change-value">{this.getValueOrLoader(rate, "change2", true)}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    
    render() {
        const drillDown = (e) => {
            drillThrough(this.props);
        };
        
        return (
            <div onClick={drillDown} className="summary summary-bar">
                {this.getStyle()}
            </div>
        );
    }
}
